import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Stack,
  Box,
  Typography,
  Button,
  Avatar,
  TextField,
  MenuItem,
  Select,
  IconButton,
  Paper,
  CircularProgress,
  Skeleton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from 'omaha-design-system';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { SportsbookLogo } from './SportsbookLogo';
import {
  Trash as DeleteIcon,
  ArrowClockwise as RefreshIcon,
  CaretDown as ExpandMoreIcon,
  CaretUp as ExpandLessIcon,
  Plus as AddIcon,
  SlidersHorizontal as TuneIcon,
  Warning as WarningIcon,
  ArrowSquareOut as OpenInNewIcon,
  MagnifyingGlass as SearchIcon,
  X as CloseIcon,
  Info as InfoIcon,
  CaretDown,
  Lock as LockIcon,
} from '@phosphor-icons/react';
import { getPropsColumns, getMobilePropsColumns, isRowLocked, PropBet } from './columns';
import { PropsUpsellCard } from './PropsUpsellCard';
import { PropsAccessContext } from '../hooks/usePropsAccess';

import { getIsMobileApp } from '../../../utils/mobileApp';
import { FilterBottomSheet } from './FilterBottomSheet';
import { PropsFilterDrawer, DrawerFilterValues } from './PropsFilterDrawer';
import { ExpandedOddsPanel } from './ExpandedOddsPanel';
import { BetSlipFab } from './BetSlipFab';
import { BetSlipBottomSheet } from './BetSlipBottomSheet';
import { StaleProjectionsBottomSheet } from './StaleProjectionsBottomSheet';
import { OtherBooksBottomSheet } from './OtherBooksBottomSheet';
import { UnlockOverlay } from './UnlockOverlay';
import { SubscriptionRequiredOverlay } from './SubscriptionRequiredOverlay';
import { CustomizeBlendDialog } from './CustomizeBlendDialog';
import { InjuryReportModal } from './InjuryReportModal';
import { useInjuryReport } from '../hooks/useInjuryReport';
import { MLBLineupsModal } from './MLBLineupsModal';
import { useLineupReport } from '../hooks/useLineupReport';
import PropsSportsbookDropdown from './PropsSportsbookDropdown';
import PropsStateDropdown from './PropsStateDropdown';
import PropsSportsDropdown from './PropsSportsDropdown';
import { IRootState } from '../../../store/reducer';
import {
  setSelectedGameIds,
  setPositionFilter,
  toggleExpandedPlayer,
  addToBetSlip,
  removeFromBetSlip,
  clearBetSlip,
  setBetType,
  setSelectedStatFilter,
  setMinMinutes,
  setPlayerSearch,
  setProjectionWeights,
  setDrawerFilters,
} from '../store/slice';
import { usePropsData } from '../hooks/usePropsData';
import { useAuth } from '../../../hooks/useAuth';
import { useSubscriptionCheck } from '../hooks/useSubscriptionCheck';
import { leagueHasMinutes, leagueHasInjuryReport, leagueHasPositionFilter, leagueGroupsGamesByKickoff } from '../data/leagueConfig';
import { groupGamesByKickoff, kickoffChipTime } from '../helpers/gameGroups';
import { BetSlipItem } from '../data/types';
import { calculateParlayStats } from '../data/probability';
import { getBetSlipDeepLinks, isActualMobileDevice, getPlatformDeepLink, openBetSlipDeepLinks } from '../data/deeplinkHelper';
import MatchupHeader from './MatchupPage/MatchupHeader';
import MatchupContent from './MatchupPage/MatchupContent';
import { LockedMatchupState, PropsOpenExplainer } from './PropsOpenState';
import { trackPropsEvent } from '../helpers/propsConversion';

// Closes over nothing — hoisted so the grid's prop identity is stable across renders
const getRowClassName = (params: { row: PropBet }) => (isRowLocked(params.row) ? 'props-row--locked' : '');


export const PropsDesktop: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth();
  const { isLoading: isSubscriptionLoading, hasActiveSubscription, checkFailed, recheck } = useSubscriptionCheck();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [betSlipSheetOpen, setBetSlipSheetOpen] = useState(false);
  const [staleWarningSheetOpen, setStaleWarningSheetOpen] = useState(false);
  const [staleWarningDismissed, setStaleWarningDismissed] = useState(false);
  const [otherBooksSheetOpen, setOtherBooksSheetOpen] = useState(false);
  const [selectedOtherBooksBet, setSelectedOtherBooksBet] = useState<PropBet | null>(null);
  const [customizeBlendOpen, setCustomizeBlendOpen] = useState(false);
  const [injuryReportOpen, setInjuryReportOpen] = useState(false);
  const [injuryReportDefaultGameId, setInjuryReportDefaultGameId] = useState<string | undefined>(undefined);
  const [lineupsModalOpen, setLineupsModalOpen] = useState(false);
  const [lineupsDefaultGameId, setLineupsDefaultGameId] = useState<string | undefined>(undefined);

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const [expandedMobileRowId, setExpandedMobileRowId] = useState<string | null>(null);
  const isUnauthed = !isAuthLoading && !isAuthenticated;
  const isMobileApp = useMemo(() => getIsMobileApp(), []);
  // Teaser (web only): signed out, or signed in without a Props subscription for this league.
  // The stokastic-site proxy has already stripped the board to one free row + locked rows;
  // this flag only switches the presentation (locked cells, no sorting, upsell card).
  // The native app never shows the teaser or any upsell (Apple Guideline 3.1.1) — it keeps
  // the sign-in / no-access screens below.
  const propsState = useSelector((s: IRootState) => s.stokasticProps);
  // The proxy served the teaser shape (it decides from the token, not from this client's check)
  const serverLocked = propsState.lockedTeaser !== null;
  // The proxy is the authority: a board it served in full is the full tool even if the client's
  // own check failed (network blip, token without an email claim) — treating that as a teaser
  // would mark every row free, bypass filters and disable sorting over real data. Signed-out
  // viewers can't receive a full board, so `isUnauthed` only covers the window before data lands.
  const isTeaser = !isMobileApp && (isUnauthed || serverLocked);
  // Client thinks subscribed, server locked the board (e.g. silent token failure): treat like a
  // failed check — retry affordance, no sales pitch.
  const accessMismatch = serverLocked && hasActiveSubscription === true;

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const pullStartY = useRef(0);
  const pullStartX = useRef(0);
  const pullDirectionLocked = useRef<'vertical' | 'horizontal' | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dataGridApiRef = useGridApiRef();
  const PULL_THRESHOLD = 80;

  const {
    filters,
    selectedGameIds,
    expandedPlayerId,
    betSlip,
    betType,
  } = propsState;

  // League-specific features (see data/leagueConfig.ts)
  const isMLB = filters.league === 'MLB';
  const showInjuryReport = leagueHasInjuryReport(filters.league);

  // On league switch the route key remounts this tree, and for the first render
  // the URL already points at the new league while Redux still holds the old
  // one (the URL sync in PropsContent runs in an effect). Anything reacting to
  // league-scoped state must wait until the two agree.
  const { league: urlLeague } = useParams<{ league: string }>();
  const isLeagueSynced = !urlLeague || urlLeague.toUpperCase() === filters.league;

  const handleDrawerApply = useCallback((values: DrawerFilterValues) => {
    dispatch(setDrawerFilters(values));
  }, [dispatch]);

  // Locked teaser rows have no odds to expand into: returning null removes the toggle entirely
  const getDetailPanelContent = useCallback(
    (params: { row: PropBet }) => (isRowLocked(params.row) ? null : <ExpandedOddsPanel row={params.row} />),
    []
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const handleRowClick = useCallback((params: { id: string | number; row: PropBet }) => {
    if (isRowLocked(params.row)) return;
    const rowId = String(params.id);
    setExpandedRowIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  }, []);

  const handleToggleMobileExpand = useCallback((rowId: string) => {
    setExpandedMobileRowId((prev) => (prev === rowId ? null : rowId));
  }, []);

  const {
    games,
    openGames,
    nextOpen,
    getGameOpenTime,
    isGameIdLocked,
    currentGame,
    sortedBets,
    isLoading,
    lastUpdatedTime,
    lastUpdatedTimestamp,
    lockedTeaser,
    freeCount,
    handleRefresh,
    getDynamicPlayerProps,
    availableStats,
    availablePositions,
  } = usePropsData({
    hasActiveSubscription,
    // The native app renders sign-in / no-access screens instead of the board — don't fetch for them
    skip: isMobileApp && hasActiveSubscription !== true,
    isTeaser,
  });

  // Day-grouped strip (NFL): game cards sit under a weekday label, so the Top Bets card and divider
  // are pushed down to line up with the cards rather than the label + cards column.
  // Desktop cards have a fixed height, so aligning to the bottom of the row is enough; the mobile row
  // stretches its items, so those get a top offset equal to the label height instead.
  const groupedStrip = leagueGroupsGamesByKickoff(filters.league) && games.length > 0;
  const MOBILE_GROUP_LABEL_OFFSET = '14px'; // 10px label + 4px spacing

  // Build dynamic stat options from available stats in the data
  const statOptions = useMemo(() => {
    const options = [{ value: 'All', label: 'All Stats' }];
    availableStats.forEach((stat) => {
      options.push({ value: stat, label: stat });
    });
    return options;
  }, [availableStats]);

  // Position filter options (NFL)
  const positionOptions = useMemo(
    () => [{ value: 'All', label: 'All Positions' }, ...availablePositions.map((p) => ({ value: p, label: p }))],
    [availablePositions]
  );

  // Injury report data - derives from games data
  const { data: injuryReportData } = useInjuryReport(openGames, filters.league);

  // MLB lineups data - derives from games data
  const { data: lineupReportData } = useLineupReport(openGames, filters.league);

  // Check if projections are stale (more than 1 hour old)
  const isProjectionsStale = useMemo(() => {
    if (!lastUpdatedTimestamp) return false;
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return lastUpdatedTimestamp < oneHourAgo;
  }, [lastUpdatedTimestamp]);

  // Auto-show stale warning when projections are stale
  useEffect(() => {
    if (isLeagueSynced && isProjectionsStale && !staleWarningDismissed && !isLoading) {
      setStaleWarningSheetOpen(true);
    }
  }, [isLeagueSynced, isProjectionsStale, staleWarningDismissed, isLoading]);

  const handleStaleWarningClose = useCallback(() => {
    setStaleWarningSheetOpen(false);
    setStaleWarningDismissed(true);
  }, []);

  const handleOtherBooksClick = useCallback((bet: PropBet) => {
    setSelectedOtherBooksBet(bet);
    setOtherBooksSheetOpen(true);
  }, []);

  const handleOtherBooksClose = useCallback(() => {
    setOtherBooksSheetOpen(false);
    setSelectedOtherBooksBet(null);
  }, []);

  // Pull-to-refresh handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = dataGridApiRef.current?.getScrollPosition?.()?.top ?? 0;
    if (scrollContainerRef.current && scrollTop === 0) {
      pullStartY.current = e.touches[0].clientY;
      pullStartX.current = e.touches[0].clientX;
      pullDirectionLocked.current = null;
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling || isLoading) return;

    const currentY = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const diffY = currentY - pullStartY.current;
    const diffX = Math.abs(currentX - pullStartX.current);

    // Lock direction on first significant movement
    if (!pullDirectionLocked.current && (Math.abs(diffY) > 10 || diffX > 10)) {
      pullDirectionLocked.current = diffX > Math.abs(diffY) ? 'horizontal' : 'vertical';
    }

    // If scrolling horizontally, cancel pull-to-refresh
    if (pullDirectionLocked.current === 'horizontal') {
      setPullDistance(0);
      return;
    }

    const scrollTop = dataGridApiRef.current?.getScrollPosition?.()?.top ?? 0;
    if (diffY > 0 && scrollTop === 0) {
      // Apply resistance to pull
      const resistance = 0.4;
      setPullDistance(Math.min(diffY * resistance, PULL_THRESHOLD * 1.5));
    }
  }, [isPulling, isLoading, PULL_THRESHOLD]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD && !isLoading && pullDirectionLocked.current !== 'horizontal') {
      handleRefresh();
      // Keep indicator visible at threshold height while loading — useEffect below resets when done
      setIsPulling(false);
      pullDirectionLocked.current = null;
      return;
    }
    setPullDistance(0);
    setIsPulling(false);
    pullDirectionLocked.current = null;
  }, [pullDistance, PULL_THRESHOLD, isLoading, handleRefresh]);

  // Collapse pull-to-refresh indicator when loading finishes
  useEffect(() => {
    if (!isLoading && pullDistance > 0 && !isPulling) {
      setPullDistance(0);
    }
  }, [isLoading, pullDistance, isPulling]);

  const handleAddToBetSlip = useCallback(
    (bet: BetSlipItem) => {
      dispatch(addToBetSlip(bet));
    },
    [dispatch]
  );

  const handleRemoveFromBetSlip = useCallback(
    (index: number) => {
      dispatch(removeFromBetSlip(index));
    },
    [dispatch]
  );

  const allBooks = new Set(betSlip.map((leg) => leg.book));
  const isSingleBook = allBooks.size <= 1;

  const { combinedOdds, trueProb, parlayEv: parlayEvPct } =
    betType === 'PARLAY' && betSlip.length >= 2 && isSingleBook
      ? calculateParlayStats(betSlip)
      : { combinedOdds: 0, trueProb: null, parlayEv: null };

  // Get deep links grouped by sportsbook for display
  const deepLinks = useMemo(() => {
    const isMobileDevice = isActualMobileDevice();
    return getBetSlipDeepLinks(betSlip, isMobileDevice);
  }, [betSlip]);

  const handleOpenSportsbook = useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  // Get deep link for a single bet
  const getBetDeepLink = useCallback((bet: BetSlipItem): string | null => {
    const isMobileDevice = isActualMobileDevice();
    return getPlatformDeepLink([bet], isMobileDevice);
  }, []);

  const currentPlayers = useMemo(() => {
    if (!currentGame) return [];
    return currentGame.players
      .map((player) => ({
        ...player,
        props: getDynamicPlayerProps(player),
      }))
      .filter((player) => player.props && player.props.length > 0)
      .sort((a, b) => {
        const maxEvA = Math.max(...(a.props?.map((p) => p.sportsbooks[0]?.evPct ?? -Infinity) || [-Infinity]));
        const maxEvB = Math.max(...(b.props?.map((p) => p.sportsbooks[0]?.evPct ?? -Infinity) || [-Infinity]));
        return maxEvB - maxEvA;
      });
  }, [currentGame, getDynamicPlayerProps]);

  // "Refresh access": re-run the entitlement check and re-request the board (the board request
  // is what carries the token the proxy decides on, so a stale/failed token gets retried too).
  const recheckAccess = useCallback(() => {
    recheck();
    void handleRefresh();
  }, [recheck, handleRefresh]);

  // Shared access state for deep components. Memoized: this component re-renders on every
  // touchmove during pull-to-refresh, and a fresh object would re-render every mounted card.
  const accessContextValue = useMemo(
    () => ({
      isTeaser,
      isAuthenticated,
      isMobileApp,
      checkFailed: checkFailed || accessMismatch,
      recheckSubscription: recheckAccess,
    }),
    [isTeaser, isAuthenticated, isMobileApp, checkFailed, accessMismatch, recheckAccess]
  );

  // Column defs rebuild only when an input changes; DataGrid compares `columns` by reference
  const gridColumns = useMemo(
    () =>
      isMobile
        ? getMobilePropsColumns(handleAddToBetSlip, betSlip, handleRemoveFromBetSlip, handleOtherBooksClick, expandedMobileRowId, handleToggleMobileExpand)
        : getPropsColumns(handleAddToBetSlip, betSlip, handleRemoveFromBetSlip, isTeaser, filters.league),
    [isMobile, handleAddToBetSlip, betSlip, handleRemoveFromBetSlip, handleOtherBooksClick, expandedMobileRowId, handleToggleMobileExpand, isTeaser, filters.league]
  );

  // One teaser impression per league visit (this tree remounts per league), not per data reload
  const impressionSentRef = useRef(false);
  useEffect(() => {
    if (!isTeaser || isLoading || !lockedTeaser || checkFailed || accessMismatch || impressionSentRef.current) return;
    impressionSentRef.current = true;
    trackPropsEvent('props_teaser_impression', {
      league: filters.league,
      positive_ev_count: lockedTeaser.count,
      max_locked_ev_pct: lockedTeaser.maxEvPct,
      is_mobile: isMobile ? 1 : 0,
      is_authenticated: isAuthenticated ? 1 : 0,
    });
  }, [isTeaser, isLoading, lockedTeaser, checkFailed, accessMismatch, filters.league, isMobile, isAuthenticated]);

  // Native app, signed out: sign-in screen (the web shows the teaser board instead)
  if (isMobileApp && isUnauthed) {
    return <UnlockOverlay />;
  }

  // Show loading while checking subscription
  if (isSubscriptionLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          backgroundColor: '#0D0E10',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
      </Box>
    );
  }

  // Native app, signed in without a Props subscription: neutral no-access screen. No teaser,
  // no upsell in-app (Apple Guideline 3.1.1). On the web this case falls through to the teaser.
  if (isMobileApp && (hasActiveSubscription === false || accessMismatch)) {
    // A failed check — or a board the server locked for someone the client thinks is
    // subscribed — is not "no access": give the (possibly subscribed) user a retry that
    // re-runs the check and re-requests the board.
    return <SubscriptionRequiredOverlay checkFailed={checkFailed || accessMismatch} onRetry={recheckAccess} />;
  }

  return (
    <PropsAccessContext.Provider value={accessContextValue}>
    <Stack sx={{ height: '100%', overflow: 'hidden', maxWidth: '100vw', backgroundColor: '#0D0E10' }}>
      {/* Header - only render on desktop */}
      {!isMobile && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 3,
            py: 2,
            backgroundColor: '#0D0E10',
          }}
        >
          {/* Left side: Dropdowns + Filters */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PropsSportsDropdown size="small" />
            <PropsSportsbookDropdown size="small" />
            <PropsStateDropdown size="small" />
            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon size={16} />}
              onClick={() => setDrawerOpen(true)}
              sx={{
                textTransform: 'none',
                fontSize: '13.33px',
                fontWeight: 600,
                py: 1,
                px: 1.5,
              }}
            >
              Filters
            </Button>
          </Stack>

          {/* Right side: Projection Blend + Customize + Updated + Schedule + Filter */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {/* Projection Blend Section */}
            <Stack spacing={0} alignItems="flex-start">
              <Typography
                sx={{
                  color: 'rgba(242, 242, 243, 0.7)',
                  fontSize: '12px',
                  fontFamily: '"Instrument Sans", sans-serif',
                }}
              >
                Projection Blend:
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                {/* Origination with info icon */}
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title="Stokastic Origination Projections: Bottom-up projections driven by proprietary player-level data and simulation-based modeling." arrow>
                    <Box component="span" sx={{ cursor: 'help', display: 'flex' }}><InfoIcon size={16} color="rgba(242, 242, 243, 0.5)" /></Box>
                  </Tooltip>
                  <Typography
                    sx={{
                      color: '#F2F2F3',
                      fontSize: '14px',
                      fontFamily: '"Instrument Sans", sans-serif',
                    }}
                  >
                    Origination {Math.round(filters.stokWeight * 100)}%
                  </Typography>
                </Stack>

                <Typography sx={{ color: 'rgba(242, 242, 243, 0.5)', fontSize: '14px' }}>•</Typography>

                {/* Market with info icon */}
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title="Stokastic Market Projections: Top-down true-odds modeling derived from optimized weighting of sharp sportsbook prices." arrow>
                    <Box component="span" sx={{ cursor: 'help', display: 'flex' }}><InfoIcon size={16} color="rgba(242, 242, 243, 0.5)" /></Box>
                  </Tooltip>
                  <Typography
                    sx={{
                      color: '#F2F2F3',
                      fontSize: '14px',
                      fontFamily: '"Instrument Sans", sans-serif',
                    }}
                  >
                    Market {Math.round(filters.marketWeight * 100)}%
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            {/* Customize Button — projection blending is a subscriber feature */}
            <Tooltip title={isTeaser ? 'Included with Stokastic Props' : ''} arrow>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isTeaser}
                  startIcon={isTeaser ? <LockIcon size={16} /> : <TuneIcon size={16} />}
                  onClick={() => setCustomizeBlendOpen(true)}
                  sx={{
                    height: 32,
                    borderRadius: '6px',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#F2F2F3',
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.4)',
                      borderColor: 'rgba(255, 255, 255, 0.12)',
                    },
                  }}
                >
                  Customize
                </Button>
              </span>
            </Tooltip>

            {/* Updated Time */}
            {lastUpdatedTime && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {isProjectionsStale && (
                  <WarningIcon size={18} color="#EF5350" />
                )}
                <Typography
                  sx={{
                    color: isProjectionsStale ? '#EF5350' : 'rgba(242, 242, 243, 0.7)',
                    fontSize: '14px',
                    fontFamily: '"Instrument Sans", sans-serif',
                  }}
                >
                  Updated
                </Typography>
                <Typography
                  sx={{
                    color: isProjectionsStale ? '#EF5350' : '#F2F2F3',
                    fontSize: '14px',
                    fontFamily: '"Instrument Sans", sans-serif',
                  }}
                >
                  {lastUpdatedTime}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      )}

      {/* Game Cards Row */}
      {!isMobile && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 3,
            py: 1.5,
            pb: 2,
            overflowX: 'scroll',
            backgroundColor: '#0D0E10',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            '&::-webkit-scrollbar': {
              height: '8px',
              display: 'block',
              WebkitAppearance: 'none',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#1C1C1E',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#3A3A3C',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#4A4A4C',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#3A3A3C #1C1C1E',
          }}
          onMouseDown={(e) => {
            const el = e.currentTarget;
            el.dataset.isDragging = 'true';
            el.dataset.startX = String(e.pageX - el.offsetLeft);
            el.dataset.scrollLeft = String(el.scrollLeft);
          }}
          onMouseMove={(e) => {
            const el = e.currentTarget;
            if (el.dataset.isDragging !== 'true') return;
            e.preventDefault();
            const x = e.pageX - el.offsetLeft;
            const walk = (x - Number(el.dataset.startX)) * 1.5;
            el.scrollLeft = Number(el.dataset.scrollLeft) - walk;
          }}
          onMouseUp={(e) => {
            e.currentTarget.dataset.isDragging = 'false';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.dataset.isDragging = 'false';
          }}
        >
          {/* Top Bets Card */}
          <Paper
            onClick={() => dispatch(setSelectedGameIds([]))}
            sx={{
              px: 3,
              py: 1,
              backgroundColor: selectedGameIds.length === 0 ? 'rgba(42, 83, 195, 0.16)' : '#212223',
              border: selectedGameIds.length === 0 ? '1px solid rgba(42, 83, 195, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              cursor: 'pointer',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 68,
              boxSizing: 'border-box',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              ...(groupedStrip && { alignSelf: 'flex-end' }),
              '&:hover': {
                backgroundColor: selectedGameIds.length === 0 ? 'rgba(42, 83, 195, 0.24)' : 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#F2F2F3',
              }}
            >
              Top Bets
            </Typography>
          </Paper>

          {/* Vertical Divider */}
          <Box
            sx={{
              width: '1px',
              height: 60,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              flexShrink: 0,
              ...(groupedStrip && { alignSelf: 'flex-end', mb: '4px' }),
            }}
          />

          {/* Game Cards */}
          {isLoading && games.length === 0 ? (
            // Skeleton loading state
            <>
              {[1, 2, 3, 4].map((i) => (
                <Paper
                  key={`skeleton-${i}`}
                  sx={{
                    px: 3,
                    py: 1,
                    backgroundColor: '#212223',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    flexShrink: 0,
                    height: 68,
                    boxSizing: 'border-box',
                    minWidth: 140,
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                  }}
                >
                  <Stack direction="column" alignItems="flex-start" spacing={0.5} sx={{ width: '100%' }}>
                    <Skeleton variant="text" width={60} height={14} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={100} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={80} height={14} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  </Stack>
                </Paper>
              ))}
            </>
          ) : (
            groupGamesByKickoff(games, filters.league).map((group) => (
            <Stack key={group.key} direction="column" spacing={0.75} sx={{ flexShrink: 0 }}>
              {group.label && (
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 600,
                    fontSize: '11px',
                    lineHeight: 1,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'rgba(242, 242, 243, 0.55)',
                    pl: 0.5,
                  }}
                >
                  {group.label}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" spacing={2}>
              {group.games.map((game) => (
              <Paper
                key={game.id}
                onClick={() => {
                  // Toggle selection - if already selected, deselect; otherwise select this game
                  if (selectedGameIds.includes(game.id)) {
                    dispatch(setSelectedGameIds([]));
                  } else {
                    dispatch(setSelectedGameIds([game.id]));
                  }
                }}
                sx={{
                  px: 1,
                  py: 1,
                  backgroundColor: selectedGameIds.includes(game.id) ? 'rgba(42, 83, 195, 0.16)' : '#212223',
                  border: selectedGameIds.includes(game.id) ? '1px solid rgba(42, 83, 195, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  height: 68,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                  // Props not open yet: still tappable (explainer page), but visually secondary
                  opacity: isGameIdLocked(game.id) && !selectedGameIds.includes(game.id) ? 0.55 : 1,
                  '&:hover': {
                    backgroundColor: selectedGameIds.includes(game.id) ? 'rgba(42, 83, 195, 0.24)' : 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/* Away Team */}
                  <Box
                    component="img"
                    src={game.awayTeam.logo}
                    alt={game.awayTeam.abbr}
                    sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'contain' }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <Stack direction="column" alignItems="center" spacing={0}>
                    <Typography
                      sx={{
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontWeight: 400,
                        fontSize: '11px',
                        lineHeight: 1,
                        color: 'rgba(242, 242, 243, 0.7)',
                      }}
                    >
                      {kickoffChipTime(game, !!group.label, isGameIdLocked(game.id) ? getGameOpenTime(game.id) : null)}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: 1.4,
                        color: '#F2F2F3',
                      }}
                    >
                      {game.awayTeam.abbr} @ {game.homeTeam.abbr}
                    </Typography>
                  </Stack>
                  {/* Home Team */}
                  <Box
                    component="img"
                    src={game.homeTeam.logo}
                    alt={game.homeTeam.abbr}
                    sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'contain' }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </Stack>
              </Paper>
              ))}
              </Stack>
            </Stack>
            ))
          )}

        </Stack>
      )}

      {/* Main Content */}
      <Stack direction="row" sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
        {/* Left Content */}
        <Stack
          sx={{ flex: 1, overflowY: isMobile ? 'hidden' : 'auto', overflowX: 'hidden', py: isMobile ? 0 : 2, backgroundColor: '#0D0E10', minWidth: 0 }}
        >
          {/* Mobile Matchup Bar - always visible on mobile */}
          {isMobile && (
            <Stack
              direction="row"
              alignItems="stretch"
              spacing={1}
              sx={{
                px: 2,
                pt: 3,
                pb: 2,
                minHeight: 48,
                overflowX: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {/* Sport Selector */}
              <Box sx={{ flexShrink: 0, ...(groupedStrip && { mt: MOBILE_GROUP_LABEL_OFFSET }) }}>
                <PropsSportsDropdown size="small" />
              </Box>

              {/* Top Bets Card */}
              <Paper
                onClick={() => dispatch(setSelectedGameIds([]))}
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: selectedGameIds.length === 0 ? 'rgba(42, 83, 195, 0.16)' : '#212223',
                  border: selectedGameIds.length === 0 ? '1px solid rgba(42, 83, 195, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                  ...(groupedStrip && { mt: MOBILE_GROUP_LABEL_OFFSET }),
                  '&:hover': {
                    backgroundColor: selectedGameIds.length === 0 ? 'rgba(42, 83, 195, 0.24)' : 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 400,
                    fontSize: '13.33px',
                    lineHeight: 1.5,
                    color: '#F2F2F3',
                  }}
                >
                  Top Bets
                </Typography>
              </Paper>

              {/* Vertical Divider */}
              <Box
                sx={{
                  width: '1px',
                  alignSelf: 'stretch',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  flexShrink: 0,
                  ...(groupedStrip && { mt: MOBILE_GROUP_LABEL_OFFSET }),
                }}
              />

              {/* Game Cards */}
              {isLoading && games.length === 0 ? (
                // Skeleton loading state
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <Paper
                      key={`skeleton-mobile-${i}`}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: '#212223',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        flexShrink: 0,
                        minWidth: 80,
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                      }}
                    >
                      <Stack direction="column" alignItems="flex-start" spacing={0.25} sx={{ width: '100%' }}>
                        <Skeleton variant="text" width={40} height={12} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Skeleton variant="text" width={70} height={16} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                      </Stack>
                    </Paper>
                  ))}
                </>
              ) : (
                groupGamesByKickoff(games, filters.league).map((group) => (
                <Stack key={group.key} direction="column" spacing={0.5} sx={{ flexShrink: 0 }}>
                  {group.label && (
                    <Typography
                      sx={{
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontWeight: 600,
                        fontSize: '10px',
                        lineHeight: 1,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'rgba(242, 242, 243, 0.55)',
                        pl: 0.5,
                      }}
                    >
                      {group.label}
                    </Typography>
                  )}
                  <Stack direction="row" alignItems="stretch" spacing={1}>
                  {group.games.map((game) => (
                  <Paper
                    key={game.id}
                    onClick={() => {
                      if (selectedGameIds.includes(game.id)) {
                        dispatch(setSelectedGameIds([]));
                      } else {
                        dispatch(setSelectedGameIds([game.id]));
                      }
                    }}
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: selectedGameIds.includes(game.id) ? 'rgba(42, 83, 195, 0.16)' : '#212223',
                      border: selectedGameIds.includes(game.id) ? '1px solid rgba(42, 83, 195, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                      opacity: isGameIdLocked(game.id) && !selectedGameIds.includes(game.id) ? 0.55 : 1,
                      '&:hover': {
                        backgroundColor: selectedGameIds.includes(game.id) ? 'rgba(42, 83, 195, 0.24)' : 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Stack direction="column" alignItems="flex-start" spacing={0}>
                      <Typography
                        sx={{
                          fontFamily: '"Instrument Sans", sans-serif',
                          fontWeight: 400,
                          fontSize: '11.11px',
                          lineHeight: 1,
                          color: 'rgba(242, 242, 243, 0.7)',
                        }}
                      >
                        {kickoffChipTime(game, !!group.label, isGameIdLocked(game.id) ? getGameOpenTime(game.id) : null)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Instrument Sans", sans-serif',
                          fontWeight: 400,
                          fontSize: '13.33px',
                          lineHeight: 1.5,
                          color: '#F2F2F3',
                        }}
                      >
                        {game.awayTeam.abbr} @ {game.homeTeam.abbr}
                      </Typography>
                    </Stack>
                  </Paper>
                  ))}
                  </Stack>
                </Stack>
                ))
              )}
            </Stack>
          )}

          {/* Show Matchup Content when a game is selected, otherwise show Top Bets */}
          {selectedGameIds.length === 1 && currentGame && isGameIdLocked(currentGame.id) ? (
            /* Selected game's props haven't opened yet (NFL open window) */
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0, px: isMobile ? 0 : 3 }}>
              <LockedMatchupState
                game={currentGame}
                openTime={getGameOpenTime(currentGame.id) ?? new Date(currentGame.startTime)}
                league={filters.league}
                onBackToOpenGames={openGames.length > 0 ? () => dispatch(setSelectedGameIds([])) : undefined}
              />
            </Stack>
          ) : selectedGameIds.length === 1 && currentGame && !isTeaser ? (
            /* Matchup View - shows players and stats for selected game.
               Teaser viewers stay on Top Bets: a selected game just filters the board
               (the matchup page is built around the subscriber-only customization tools). */
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0, px: isMobile ? 0 : 3 }}>
              <MatchupHeader
                game={currentGame}
                onOpenInjuryReport={() => {
                  setInjuryReportDefaultGameId(currentGame.id);
                  setInjuryReportOpen(true);
                }}
                onOpenLineups={() => {
                  setLineupsDefaultGameId(currentGame.id);
                  setLineupsModalOpen(true);
                }}
              />
              <MatchupContent game={currentGame} />
            </Stack>
          ) : (
            /* Top Bets View (all games or filtered by selectedGameIds) */
            <Stack spacing={isMobile ? 0 : 2} sx={{ height: '100%', px: isMobile ? 0 : 3 }}>
              {/* Filters */}
              {isMobile ? (
                /* Mobile Layout */
                <Stack spacing={2} sx={{ mb: 1 }}>
                  {/* Row 2: Stats dropdown + Filter button | Projections Updated */}
                  <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} spacing={1} sx={{ px: 2, overflow: 'hidden' }}>
                    {/* Left: Stats Dropdown + Filter Button */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Select
                        value={filters.selectedStatFilter}
                        onChange={(e) => dispatch(setSelectedStatFilter(e.target.value))}
                        size="small"
                        variant="outlined"
                        displayEmpty
                        IconComponent={(props) => (
                          <CaretDown
                            {...props}
                            size={16}
                            weight="regular"
                            style={{ position: 'absolute', right: 11.11, pointerEvents: 'none' }}
                          />
                        )}
                        renderValue={(value) => {
                          const opt = statOptions.find((o) => o.value === value);
                          return opt?.label || 'All Stats';
                        }}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: '#F2F2F3',
                          borderRadius: '5.555px',
                          height: '38.85px',
                          fontFamily: '"Instrument Sans", sans-serif',
                          fontSize: '13.33px',
                          fontWeight: 400,
                          lineHeight: 1.5,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '& .MuiSelect-select': {
                            py: '5.555px',
                            px: '11.11px',
                            pr: '33px !important',
                            fontSize: '13.33px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                          '& .MuiSelect-icon': {
                            color: '#F2F2F3',
                          },
                        }}
                      >
                        {statOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>

                      <IconButton
                        onClick={() => setFilterSheetOpen(true)}
                        sx={{
                          color: '#f1f2f2',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          width: 44,
                          height: 44,
                        }}
                      >
                        <TuneIcon />
                      </IconButton>
                    </Stack>

                    {/* Right: Projections Updated */}
                    <Typography
                      sx={{
                        color: 'rgba(242, 242, 243, 0.7)',
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontSize: '13.33px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600, color: '#F2F2F3' }}>
                        Projections Updated
                      </Box>{' '}
                      {lastUpdatedTime || ''}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                /* Desktop Layout - Filter bar */
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                >
                  <TextField
                    label="Market"
                    select
                    variant="filled"
                    size="small"
                    value={filters.selectedStatFilter}
                    onChange={(e) => dispatch(setSelectedStatFilter(e.target.value))}
                    sx={{
                      width: 120,
                    
                    }}
                  >
                    {statOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {leagueHasPositionFilter(filters.league) && (
                    <TextField
                      label="Position"
                      select
                      variant="filled"
                      size="small"
                      value={positionOptions.some((o) => o.value === filters.positionFilter) ? filters.positionFilter : 'All'}
                      onChange={(e) => dispatch(setPositionFilter(e.target.value))}
                      sx={{ width: 130 }}
                    >
                      {positionOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {leagueHasMinutes(filters.league) && (
                    <TextField
                      label="Min Player Minutes:"
                      type="number"
                      variant="filled"
                      size="small"
                      value={filters.minMinutes || ''}
                      onChange={(e) => dispatch(setMinMinutes(Math.max(0, parseFloat(e.target.value) || 0)))}
                      inputProps={{ min: 0 }}
                      sx={{
                        width: 120,
                      }}
                    />
                  )}

                  <TextField
                    placeholder="Search..."
                    variant="filled"
                    size="small"
                    hiddenLabel
                    value={filters.playerSearch}
                    onChange={(e) => dispatch(setPlayerSearch(e.target.value))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon size={20} color="rgba(255,255,255,0.5)" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: 180,
                      '& .MuiFilledInput-root': {
                        paddingTop: 0,
                      },
                      '& .MuiFilledInput-input': {
                        paddingTop: '10px',
                        paddingBottom: '10px',
                      },
                    }}
                  />

                  {/* Injury Report Button - NBA and NFL */}
                  {showInjuryReport && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setInjuryReportOpen(true)}
                      sx={{
                        borderColor: 'rgba(242, 242, 243, 0.38)',
                        color: 'text.primary',
                        textTransform: 'none',
                        fontSize: '13.33px',
                        fontWeight: 600,
                        py: 1,
                        px: 1.5,
                        '&:hover': {
                          borderColor: 'rgba(242, 242, 243, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      Injury Report
                    </Button>
                  )}

                  {/* MLB Lineups Button */}
                  {isMLB && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setLineupsModalOpen(true)}
                      sx={{
                        borderColor: 'rgba(242, 242, 243, 0.38)',
                        color: 'text.primary',
                        textTransform: 'none',
                        fontSize: '13.33px',
                        fontWeight: 600,
                        py: 1,
                        px: 1.5,
                        '&:hover': {
                          borderColor: 'rgba(242, 242, 243, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      Lineups
                    </Button>
                  )}

                  <IconButton
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{ color: '#42A5F5' }}
                  >
                    {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Stack>
              )}

              {/* Teaser upsell — above the board like the OddsShopper arb taster (its best-converting
                  surface). The grid below keeps the free row + locked rows; the card carries the top
                  locked plays with their EV. Quantified from the server's lockedTeaser summary. */}
              {/* Only when the proxy actually served the teaser shape: if an unstripped board ever
                  arrives (proxy not deployed yet, dev mock), claiming rows are "locked" would be false. */}
              {isTeaser && !isLoading && lockedTeaser && (
                <PropsUpsellCard
                  league={filters.league}
                  lockedTeaser={lockedTeaser}
                  freeCount={freeCount}
                  isMobile={isMobile}
                  onSignIn={() => login(`/props/${filters.league}`)}
                />
              )}

              {/* DataGrid */}
              {/* Pull-to-refresh indicator (mobile only) */}
              {isMobile && (
                <Box
                  sx={{
                    height: pullDistance || (isLoading && !isPulling ? PULL_THRESHOLD : 0),
                    minHeight: (pullDistance > 0 || (isLoading && !isPulling)) ? 40 : 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    transition: isPulling ? 'none' : 'height 0.2s ease-out',
                  }}
                >
                  {(pullDistance > 0 || (isLoading && !isPulling)) && (
                    <Stack alignItems="center" spacing={0.5}>
                      <CircularProgress
                        size={24}
                        sx={{
                          color: '#42A5F5',
                          opacity: Math.min(pullDistance / PULL_THRESHOLD, 1),
                          transform: `rotate(${(pullDistance / PULL_THRESHOLD) * 360}deg)`,
                        }}
                        variant={isLoading ? 'indeterminate' : 'determinate'}
                        value={isLoading ? undefined : Math.min((pullDistance / PULL_THRESHOLD) * 100, 100)}
                      />
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: 'rgba(242, 242, 243, 0.7)',
                          opacity: Math.min(pullDistance / PULL_THRESHOLD, 1),
                        }}
                      >
                        {pullDistance >= PULL_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              )}
              <Box
                ref={isMobile ? scrollContainerRef : undefined}
                onTouchStart={isMobile ? handleTouchStart : undefined}
                onTouchMove={isMobile ? handleTouchMove : undefined}
                onTouchEnd={isMobile ? handleTouchEnd : undefined}
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 400,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    backgroundColor: isMobile ? 'transparent' : '#161617',
                    borderRadius: isMobile ? 0 : 2,
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    color: '#f1f2f2',
                  },
                  '& .MuiDataGrid-row--detailPanelExpanded .MuiDataGrid-cell': {
                    borderBottom: 'none',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#1C1C1E',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    color: '#f2f2f3b2',
                    '&:focus': { outline: 'none' },
                    '&:focus-within': { outline: 'none' },
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: 13,
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
                  },
                  '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: 'rgba(42, 83, 195, 0.16)',
                    '&:hover': { backgroundColor: 'rgba(42, 83, 195, 0.24)' },
                  },
                  '& .MuiTablePagination-root': {
                    color: '#f2f2f3b2',
                  },
                  '& .MuiTablePagination-selectIcon': {
                    color: '#f2f2f3b2',
                  },
                  '& .MuiIconButton-root': {
                    color: '#f2f2f3b2',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    backgroundColor: isMobile ? 'transparent' : '#161617',
                  },
                  '& .MuiDataGrid-sortIcon': {
                    color: '#42a5f5',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    justifyContent: 'flex-start',
                    borderTop: 'none',
                    minHeight: 'auto',
                  },
                  '& .MuiTablePagination-spacer': {
                    display: 'none',
                  },
                  '& .MuiTablePagination-selectLabel': {
                    color: 'rgba(242, 242, 243, 0.7)',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    color: '#F2F2F3',
                  },
                  '& .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiTablePagination-actions .MuiIconButton-root': {
                    color: '#42A5F5',
                  },
                  '& .MuiTablePagination-toolbar': {
                    minHeight: 'auto',
                    padding: '8px 0',
                    marginLeft: '16px',
                  },
                  '& .MuiTablePagination-toolbar .MuiInputBase-root': {
                    border: '1px solid #42A5F5',
                    borderRadius: '8px',
                    height: 32,
                    minHeight: 32,
                    '& .MuiSelect-select': {
                      padding: '4px 24px 4px 8px',
                      height: 'auto',
                      minHeight: 'auto',
                    },
                  },
                  '& .MuiTablePagination-actions .MuiIconButton-root': {
                    border: '1px solid #42A5F5',
                    borderRadius: '8px',
                    height: 32,
                    width: 32,
                    padding: 0,
                    margin: 0,
                  },
                  '& .MuiTablePagination-actions .MuiIconButton-root:last-of-type': {
                    marginLeft: '8px',
                  },
                }}
              >
                {openGames.length === 0 && games.length > 0 && !isLoading ? (
                  /* Every game on the slate is still inside the props open window */
                  <PropsOpenExplainer league={filters.league} nextOpen={nextOpen} />
                ) : (
                  <DataGridPro
                    apiRef={dataGridApiRef}
                    // Teaser: the free row leads, locked rows follow (values withheld server-side)
                    rows={sortedBets.map((bet, idx) => ({
                      id: `${bet.playerId}-${bet.stat}-${idx}`,
                      ...bet,
                    }))}
                    columns={gridColumns}
                    loading={isLoading}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    rowHeight={isMobile ? undefined : 56}
                    getRowHeight={isMobile ? () => 'auto' as const : undefined}
                    columnHeaderHeight={isMobile ? 0 : 56}
                    getRowClassName={getRowClassName}
                    {...(!isMobile && {
                      getDetailPanelContent,
                      getDetailPanelHeight,
                      detailPanelExpandedRowIds: expandedRowIds,
                      onDetailPanelExpandedRowIdsChange: (ids: (string | number)[]) => setExpandedRowIds(ids.map(String)),
                      onRowClick: handleRowClick,
                      slots: {
                        detailPanelExpandIcon: () => <CaretDown size={16} weight="bold" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />,
                        detailPanelCollapseIcon: () => <CaretDown size={16} weight="bold" style={{ color: 'rgba(255, 255, 255, 0.5)', transform: 'rotate(180deg)' }} />,
                      },
                    })}
                    initialState={{
                      sorting: {
                        sortModel: [{ field: 'evPct', sort: 'desc' }],
                      },
                    }}
                    pageSizeOptions={isMobile ? [1000] : [10, 25, 50, 100]}
                    pagination={!isMobile}
                    paginationModel={isMobile ? undefined : { page, pageSize }}
                    onPaginationModelChange={isMobile ? undefined : (model) => {
                      setPage(model.page);
                      setPageSize(model.pageSize);
                    }}
                    hideFooter={isMobile}
                    sx={{
                      height: '100%',
                      width: '100%',
                      maxWidth: '100%',
                      '& .MuiDataGrid-cell:focus': { outline: 'none' },
                      '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                      '& .MuiDataGrid-detailPanel': {
                        backgroundColor: '#1a1a1b',
                      },
                      // Locked teaser rows are inert: no hover, and no (disabled) detail toggle
                      '& .MuiDataGrid-row.props-row--locked': {
                        cursor: 'default',
                        '&:hover': { backgroundColor: 'transparent' },
                        '& .MuiDataGrid-detailPanelToggleCell': { visibility: 'hidden' },
                      },
                      ...(!isMobile && {
                        '& .MuiDataGrid-row': {
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          },
                        },
                      }),
                      ...(isMobile && {
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': { display: 'none' },
                        '& .MuiDataGrid-virtualScroller': {
                          marginTop: '0 !important',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                          width: '100% !important',
                          maxWidth: '100% !important',
                          overflow: 'hidden',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'hidden',
                        },
                        '& .MuiDataGrid-row': {
                          borderBottom: 'none',
                          width: '100%',
                          maxWidth: '100%',
                          marginBottom: '4px',
                          backgroundColor: 'transparent',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                          '& .MuiDataGrid-cell': {
                            borderBottom: 'none',
                            padding: '4px 16px',
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: '0 !important',
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                          },
                        },
                        '& .MuiDataGrid-footerContainer': { display: 'none' },
                        '& .MuiDataGrid-overlay': {
                          backgroundColor: 'transparent',
                        },
                      }),
                    }}
                  />
                )}
              </Box>
            </Stack>
          )}
        </Stack>

        {/* Bet Slip Sidebar - floating card */}
        {!isMobile && (
          <Box
            sx={{
              width: betSlipSheetOpen && betSlip.length > 0 ? 336 : 0,
              minWidth: betSlipSheetOpen && betSlip.length > 0 ? 336 : 0,
              overflow: 'hidden',
              transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out',
              p: betSlipSheetOpen && betSlip.length > 0 ? 2 : 0,
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <Stack
              sx={{
                width: 320,
                minWidth: 320,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#161617',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                overflow: 'hidden',
              }}
            >
              <Stack sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.12)' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setBetSlipSheetOpen(false)}
                      sx={{ color: 'rgba(242, 242, 243, 0.7)', p: 0.5 }}
                    >
                      <CloseIcon size={20} />
                    </IconButton>
                    <Typography
                      sx={{
                        fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                        fontWeight: 900,
                        fontSize: '23.04px',
                        lineHeight: 1.248,
                        color: '#F2F2F3',
                      }}
                    >
                      Bet Slip
                    </Typography>
                  </Stack>
                  {betSlip.length > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => dispatch(clearBetSlip())}
                      sx={{
                        color: '#EF5350',
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontWeight: 600,
                        fontSize: '13.33px',
                        textTransform: 'none',
                        padding: '5.55px',
                        minWidth: 'auto',
                      }}
                    >
                      Clear Slip
                    </Button>
                  )}
                </Stack>

                {betSlip.length >= 2 && (
                  <ToggleButtonGroup
                    value={betType}
                    exclusive
                    onChange={(_, newType) => newType && dispatch(setBetType(newType))}
                    fullWidth
                    sx={{
                      mt: 2,
                      '& .MuiToggleButtonGroup-grouped': {
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        '&:first-of-type': {
                          borderRadius: '8px 0 0 8px',
                        },
                        '&:last-of-type': {
                          borderRadius: '0 8px 8px 0',
                        },
                        '&:not(:first-of-type)': {
                          borderLeft: '1px solid rgba(255, 255, 255, 0.12)',
                          marginLeft: 0,
                        },
                      },
                    }}
                  >
                    <ToggleButton
                      value="STRAIGHT"
                      sx={{
                        flex: 1,
                        color: '#F2F2F3',
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontWeight: 400,
                        fontSize: '13.33px',
                        textTransform: 'none',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(255, 255, 255, 0.16)',
                          color: '#F2F2F3',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      }}
                    >
                      Straight
                    </ToggleButton>
                    <ToggleButton
                      value="PARLAY"
                      disabled={!isSingleBook}
                      sx={{
                        flex: 1,
                        color: isSingleBook ? '#F2F2F3' : 'rgba(242, 242, 243, 0.38)',
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontWeight: 400,
                        fontSize: '13.33px',
                        textTransform: 'none',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(255, 255, 255, 0.16)',
                          color: '#F2F2F3',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      }}
                    >
                      Parlay
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>

              <Stack sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
                <Stack spacing={1}>
                  {betSlip.map((bet, index) => (
                    <Box
                      key={bet.id}
                      sx={{
                        backgroundColor: '#212223',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '8px',
                        p: 2,
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        {/* Avatar */}
                        <Avatar
                          src={bet.avatar}
                          sx={{ width: 32, height: 32 }}
                        >
                          {bet.playerName.split(' ').map((n) => n[0]).join('')}
                        </Avatar>

                        {/* Content */}
                        <Stack sx={{ flex: 1 }} spacing={0.5}>
                          {/* Player name row with action icons */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                              sx={{
                                fontFamily: '"Instrument Sans", sans-serif',
                                fontWeight: 600,
                                fontSize: '13.33px',
                                lineHeight: 1.5,
                                color: '#F2F2F3',
                              }}
                            >
                              {bet.playerName}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {(() => {
                                const betUrl = getBetDeepLink(bet);
                                return betUrl ? (
                                  <IconButton
                                    onClick={() => handleOpenSportsbook(betUrl)}
                                    sx={{
                                      color: '#42A5F5',
                                      p: 0.25,
                                      '&:hover': { color: '#1976d2' },
                                    }}
                                  >
                                    <OpenInNewIcon size={20} />
                                  </IconButton>
                                ) : null;
                              })()}
                              <IconButton
                                onClick={() => handleRemoveFromBetSlip(index)}
                                sx={{
                                  color: '#F2F2F3',
                                  p: 0.25,
                                }}
                              >
                                <DeleteIcon size={20} />
                              </IconButton>
                            </Stack>
                          </Stack>

                          {/* Market info row */}
                          <Stack direction="row" justifyContent="space-between">
                            <Typography
                              sx={{
                                fontFamily: '"Instrument Sans", sans-serif',
                                fontWeight: 400,
                                fontSize: '13.33px',
                                lineHeight: 1.5,
                                color: 'rgba(242, 242, 243, 0.7)',
                              }}
                            >
                              {bet.stat} {bet.side === 'Under' ? 'u' : 'o'}{bet.bookLine}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography
                                sx={{
                                  fontFamily: '"Instrument Sans", sans-serif',
                                  fontWeight: 400,
                                  fontSize: '13.33px',
                                  lineHeight: 1.5,
                                  color: 'rgba(242, 242, 243, 0.7)',
                                }}
                              >
                                {bet.odds}
                              </Typography>
                              <SportsbookLogo book={bet.book} bookIcon={bet.bookLogo} size={18} borderRadius="3px" />
                            </Stack>
                          </Stack>

                          {/* Projection and EV row */}
                          <Stack direction="row" justifyContent="space-between">
                            <Typography
                              sx={{
                                fontFamily: '"Instrument Sans", sans-serif',
                                fontWeight: 400,
                                fontSize: '13.33px',
                                lineHeight: 1.5,
                                color: '#42A5F5',
                              }}
                            >
                              Proj: {bet.projection}
                            </Typography>
                            {bet.evPct !== null && (
                              <Typography
                                sx={{
                                  fontFamily: '"Instrument Sans", sans-serif',
                                  fontWeight: 400,
                                  fontSize: '13.33px',
                                  lineHeight: 1.5,
                                  color: '#66BB6A',
                                }}
                              >
                                {bet.evPct >= 0 ? '+' : ''}{bet.evPct.toFixed(1)}% EV
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>

              <Stack sx={{ p: 1, borderTop: '1px solid rgba(255, 255, 255, 0.12)' }}>
                {betType === 'PARLAY' && betSlip.length >= 2 && isSingleBook && (
                  <Stack sx={{ mb: 1, px: 1 }} spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography
                        sx={{
                          fontFamily: '"Instrument Sans", sans-serif',
                          fontWeight: 400,
                          fontSize: '13.33px',
                          color: 'rgba(242, 242, 243, 0.7)',
                        }}
                      >
                        Combined Odds
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Instrument Sans", sans-serif',
                          fontWeight: 400,
                          fontSize: '13.33px',
                          color: '#F2F2F3',
                        }}
                      >
                        {combinedOdds > 0 ? '+' : ''}{combinedOdds} {betSlip[0]?.book || ''}
                      </Typography>
                    </Stack>
                    {trueProb !== null && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography
                          sx={{
                            fontFamily: '"Instrument Sans", sans-serif',
                            fontWeight: 400,
                            fontSize: '13.33px',
                            color: 'rgba(242, 242, 243, 0.7)',
                          }}
                        >
                          Model Win %
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Instrument Sans", sans-serif',
                            fontWeight: 400,
                            fontSize: '13.33px',
                            color: '#42A5F5',
                          }}
                        >
                          {trueProb.toFixed(2)}%
                        </Typography>
                      </Stack>
                    )}
                    {parlayEvPct !== null && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography
                          sx={{
                            fontFamily: '"Instrument Sans", sans-serif',
                            fontWeight: 400,
                            fontSize: '13.33px',
                            color: 'rgba(242, 242, 243, 0.7)',
                          }}
                        >
                          Parlay EV
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Instrument Sans", sans-serif',
                            fontWeight: 400,
                            fontSize: '13.33px',
                            color: '#66BB6A',
                          }}
                        >
                          {parlayEvPct >= 0 ? '+' : ''}{parlayEvPct.toFixed(1)}%
                        </Typography>
                      </Stack>
                    )}
                    {/* SGP Correlation Disclaimer */}
                    <Typography
                      sx={{
                        fontFamily: '"Instrument Sans", sans-serif',
                        fontWeight: 400,
                        fontSize: '11px',
                        fontStyle: 'italic',
                        color: 'rgba(242, 242, 243, 0.5)',
                        textAlign: 'center',
                        pt: 1,
                      }}
                    >
                      Note: Model Win % and EV do not account for SGP correlation.
                    </Typography>
                  </Stack>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<OpenInNewIcon size={16} />}
                  onClick={() => openBetSlipDeepLinks(betSlip)}
                  sx={{
                    height: 46.62,
                    borderRadius: '6.67px',
                    backgroundColor: '#42A5F5',
                    border: '1px solid rgba(42, 83, 195, 0.3)',
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 600,
                    fontSize: '13.33px',
                    color: 'rgba(0, 0, 0, 0.87)',
                    textTransform: 'none',
                    boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.06), 0px 9px 9px 0px rgba(0, 0, 0, 0.05), 0px 21px 13px 0px rgba(0, 0, 0, 0.03), 0px 38px 15px 0px rgba(0, 0, 0, 0.01), 0px 59px 16px 0px rgba(0, 0, 0, 0)',
                    '&:hover': { backgroundColor: '#1976d2' },
                  }}
                >
                  {betType === 'PARLAY' && isSingleBook
                    ? `Place ${betSlip.length}-Leg Parlay`
                    : `Place ${betSlip.length} Straight Bet${betSlip.length > 1 ? 's' : ''}`}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>

      {/* Desktop Filter Drawer */}
      <PropsFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onApply={handleDrawerApply}
      />

      {/* Mobile Filter Bottom Sheet */}
      <FilterBottomSheet
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        onOpenInjuryReport={showInjuryReport ? () => setInjuryReportOpen(true) : undefined}
        onOpenLineups={isMLB ? () => setLineupsModalOpen(true) : undefined}
      />

      {/* Bet Slip FAB */}
      {!betSlipSheetOpen && (
        <BetSlipFab
          count={betSlip.length}
          onClick={() => setBetSlipSheetOpen(true)}
        />
      )}

      {/* Mobile Bet Slip Bottom Sheet */}
      {isMobile && (
        <BetSlipBottomSheet
          open={betSlipSheetOpen}
          onClose={() => setBetSlipSheetOpen(false)}
        />
      )}

      {/* Stale Projections Warning Bottom Sheet */}
      <StaleProjectionsBottomSheet
        open={staleWarningSheetOpen}
        onClose={handleStaleWarningClose}
        lastUpdatedTime={lastUpdatedTime}
      />

      {/* Other Books Bottom Sheet */}
      {selectedOtherBooksBet && (
        <OtherBooksBottomSheet
          open={otherBooksSheetOpen}
          onClose={handleOtherBooksClose}
          playerName={selectedOtherBooksBet.playerName}
          playerId={selectedOtherBooksBet.playerId}
          avatar={selectedOtherBooksBet.avatar}
          stat={selectedOtherBooksBet.stat}
          side={selectedOtherBooksBet.side}
          projection={selectedOtherBooksBet.projection}
          sportsbooks={selectedOtherBooksBet.sportsbooks}
          onAddToBetSlip={handleAddToBetSlip}
          betSlip={betSlip}
        />
      )}
      {/* Customize Blend Dialog (subscribers only) */}
      <CustomizeBlendDialog
        open={customizeBlendOpen && !isTeaser}
        onClose={() => setCustomizeBlendOpen(false)}
        stokWeight={filters.stokWeight}
        marketWeight={filters.marketWeight}
        onApply={(stokWeight, marketWeight) => {
          dispatch(setProjectionWeights({ stokWeight, marketWeight }));
        }}
      />

      {/* Injury Report Modal */}
      <InjuryReportModal
        open={injuryReportOpen}
        onClose={() => {
          setInjuryReportOpen(false);
          setInjuryReportDefaultGameId(undefined);
        }}
        data={injuryReportData ?? undefined}
        isLoading={isLoading}
        defaultExpandedGameId={injuryReportDefaultGameId}
        league={filters.league}
      />

      {/* MLB Lineups Modal */}
      <MLBLineupsModal
        open={lineupsModalOpen}
        onClose={() => {
          setLineupsModalOpen(false);
          setLineupsDefaultGameId(undefined);
        }}
        data={lineupReportData ?? undefined}
        isLoading={isLoading}
        defaultExpandedGameId={lineupsDefaultGameId}
      />
    </Stack>
    </PropsAccessContext.Provider>
  );
};

export default PropsDesktop;
